import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageLoader from '../components/common/PageLoader';

// Lazy-loaded pages
const GoogleAuthCallbackPage = lazy(() => import('../pages/GoogleAuthCallbackPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('../pages/VerifyEmailPage'));
const GoogleCompleteProfilePage = lazy(() => import('../pages/GoogleCompleteProfilePage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const StoreDetailsPage = lazy(() => import('../pages/StoreDetailsPage'));
const StoreLocationPage = lazy(() => import('../pages/StoreLocationPage'));
const StorePaymentPage = lazy(() => import('../pages/StorePaymentPage'));
const AddStorePage = lazy(() => import('../pages/AddStorePage'));
const SellerDashboard = lazy(() => import('../components/dashboard/SellerDashboard'));
const ProductsPage = lazy(() => import('../pages/ProductsPage'));
const ViewProductPage = lazy(() => import('../pages/ViewProductPage'));
const OrdersPage = lazy(() => import('../pages/OrdersPage'));
const OrderDetailsPage = lazy(() => import('../pages/OrderDetailsPage'));
const CustomersPage = lazy(() => import('../pages/CustomersPage'));
const CustomerDetailsPage = lazy(() => import('../pages/CustomerDetailsPage'));
const AnalyticsPage = lazy(() => import('../pages/AnalyticsPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const MessagesPage = lazy(() => import('../pages/MessagesPage'));
const SmsSettingsPage = lazy(() => import('../pages/SmsSettingsPage'));
const PayoutsPage = lazy(() => import('../pages/PayoutsPage'));
const ReviewsPage = lazy(() => import('../pages/ReviewsPage'));
const RecordSalePage = lazy(() => import('../pages/RecordSalePage'));
const SalesPage = lazy(() => import('../pages/SalesPage'));

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  REGISTER_STORE: '/register/store',
  REGISTER_STORE_LOCATION: '/register/store/location',
  REGISTER_STORE_PAYMENT: '/register/store/payment',
  DASHBOARD: '/dashboard',
  PRODUCTS: '/dashboard/products',
  PRODUCT: (id) => `/dashboard/products/${id}`,
  ORDERS: '/dashboard/orders',
  ORDER: (id) => `/dashboard/orders/${id}`,
  RECORD_SALE: '/dashboard/orders/record-sale',
  SALES: '/dashboard/sales',
  CUSTOMERS: '/dashboard/customers',
  CUSTOMER: (id) => `/dashboard/customers/${id}`,
  MESSAGES: '/dashboard/messages',
  ANALYTICS: '/dashboard/analytics',
  SETTINGS: '/dashboard/settings',
  SMS: '/dashboard/sms',
  PAYOUTS: '/dashboard/payouts',
  REVIEWS: '/dashboard/reviews',
  ADD_STORE: '/dashboard/stores/add',
  ADD_STORE_LOCATION: '/dashboard/stores/add/location',
  ADD_STORE_PAYMENT: '/dashboard/stores/add/payment'
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.LOGIN} replace />} />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/google/callback" element={<GoogleAuthCallbackPage />} />
        <Route path="/register/google-complete" element={<GoogleCompleteProfilePage />} />

        <Route path={ROUTES.REGISTER_STORE} element={<ProtectedRoute><StoreDetailsPage /></ProtectedRoute>} />
        <Route path={ROUTES.REGISTER_STORE_LOCATION} element={<ProtectedRoute><StoreLocationPage /></ProtectedRoute>} />
        <Route path={ROUTES.REGISTER_STORE_PAYMENT} element={<ProtectedRoute><StorePaymentPage /></ProtectedRoute>} />

        <Route path={ROUTES.DASHBOARD} element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<SellerDashboard />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/:productId" element={<ViewProductPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/record-sale" element={<RecordSalePage />} />
          <Route path="sales" element={<SalesPage />} />
          <Route path="orders/:orderId" element={<OrderDetailsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:customerId" element={<CustomerDetailsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="sms" element={<SmsSettingsPage />} />
          <Route path="payouts" element={<PayoutsPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="stores/add" element={<AddStorePage />} />
          <Route path="stores/add/location" element={<StoreLocationPage />} />
          <Route path="stores/add/payment" element={<StorePaymentPage />} />
          <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
        </Route>

        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
